/**
 * Executive prompt library + executive config API client (Trinity-facing
 * authoring surface inside the analyst AI tab).
 *
 * Library CRUD + health live on Agentic-API (/v2/library — raw-SQL owner
 * of the ExecutivePrompt table); the executive brand/scenario config
 * lives on the main API (/v2/instances/<id>/executive_config).
 *
 * Hand-maintained against:
 *  - Agentic-API app/controllers/v2/library.py
 *  - API src/controllers/v2/executive.py
 */
import { API_TYPE, create, load, patch, put, remove } from 'src/services/API';

export interface LibraryPrompt {
  id: number;
  model_instance_id: number;
  title: string;
  subtitle: string | null;
  category: string;
  sort_order: number;
  enabled: 0 | 1;
  tool: string;
  params: string;
  source_plan_id: string | null;
  created_by_user_id: number;
  inserted_at: string | null;
  updated_at: string | null;
}

export interface LibraryHealthResult {
  prompt_id: number;
  healthy: boolean;
  errors: string[];
}

// Read-only informational list: the tagged scenarios executives will
// see for this instance. There is no per-scenario executive config.
export interface ExecutiveConfigScenario {
  scenario_id: number;
  name: string;
  tag: string | null;
}

export interface ExecutiveConfig {
  enabled: boolean;
  subheader: string | null;
  // Exec AI year window (C1): years before / after the current calendar year
  // the Exec time-series charts + Excel exports span. null = use the global
  // default (no clip). Set here in the Executive View Management modal.
  exec_ai_years_before: number | null;
  exec_ai_years_after: number | null;
  scenarios: ExecutiveConfigScenario[];
}

// What-If curation (main API): AI-enabled candidate outputs/inputs for an
// instance, each carrying its current IsExecutiveVisible flag.
export interface WhatIfCurationEntity {
  id: number;
  name: string;
  is_executive_visible: boolean;
}

export interface WhatIfCuration {
  outputs: WhatIfCurationEntity[];
  inputs: WhatIfCurationEntity[];
}

// What-If examples (Agentic-API): ExecutivePrompt rows discriminated by
// tool='executive_whatif_example'. Params carries {output_id, input_id,
// change_text}; Title is the display sentence. Task 10 has landed on
// Agentic-API (app/controllers/v2/library.py): GET /v2/library accepts
// a `tool` query filter, POST /v2/library/examples exists, and
// POST /v2/library/health resolves both CV prompts and example rows
// (via `_check_example_prompt_bindings`) in one call — see call-site
// comments below.
export const WHATIF_EXAMPLE_TOOL = 'executive_whatif_example';

export interface WhatIfExampleParams {
  output_id: number;
  input_id: number;
  change_text: string;
}

export interface WhatIfExample extends LibraryPrompt {
  tool: typeof WHATIF_EXAMPLE_TOOL;
}

// ---------------------------------------------------------------------------
// Library (Agentic-API)
// ---------------------------------------------------------------------------

export async function saveToLibrary(
  planId: string,
  title: string,
  subtitle: string | null,
  category: string
): Promise<{ prompt_id: number }> {
  return create<{ prompt_id: number }>(
    '/v2/library',
    { plan_id: planId, title, subtitle, category },
    API_TYPE.AGENTIC
  );
}

export async function listLibraryPrompts(
  modelInstanceId: number,
  tool?: string
): Promise<{ prompts: LibraryPrompt[] }> {
  // No `tool` keeps the server default (chart_visualization) so the Prompts
  // tab's listing is unchanged; pass a tool to manage another prompt type.
  const toolParam = tool ? `&tool=${tool}` : '';
  return load(
    `/v2/library?model_instance_id=${modelInstanceId}${toolParam}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<{ prompts: LibraryPrompt[] }>;
}

export async function patchLibraryPrompt(
  promptId: number,
  fields: Partial<{
    title: string;
    subtitle: string | null;
    category: string;
    sort_order: number;
    enabled: 0 | 1;
  }>
): Promise<{ prompt: LibraryPrompt }> {
  return patch<{ prompt: LibraryPrompt }>(
    `/v2/library/${promptId}`,
    fields,
    API_TYPE.AGENTIC
  );
}

export async function deleteLibraryPrompt(promptId: number): Promise<void> {
  await remove(`/v2/library/${promptId}`, API_TYPE.AGENTIC);
}

export async function libraryHealthCheck(
  modelInstanceId: number
): Promise<{ results: LibraryHealthResult[] }> {
  return create<{ results: LibraryHealthResult[] }>(
    '/v2/library/health',
    { model_instance_id: modelInstanceId },
    API_TYPE.AGENTIC
  );
}

// ---------------------------------------------------------------------------
// Executive config (main API)
// ---------------------------------------------------------------------------

export async function getExecutiveConfig(
  modelInstanceId: number
): Promise<ExecutiveConfig> {
  return load(
    `/v2/instances/${modelInstanceId}/executive_config`,
    undefined,
    API_TYPE.DEFAULT
  ) as Promise<ExecutiveConfig>;
}

export async function putExecutiveConfig(
  modelInstanceId: number,
  payload: {
    enabled: boolean;
    subheader: string | null;
    // Exec AI year window (C1). Omit a key to leave it untouched; null clears
    // it (back to the global default); a non-negative int sets it.
    exec_ai_years_before?: number | null;
    exec_ai_years_after?: number | null;
  }
): Promise<{ status: string }> {
  return put<{ status: string }>(
    `/v2/instances/${modelInstanceId}/executive_config`,
    payload,
    API_TYPE.DEFAULT
  );
}

// ---------------------------------------------------------------------------
// What-If curation (main API)
// ---------------------------------------------------------------------------

export async function getWhatIfCuration(
  modelInstanceId: number
): Promise<WhatIfCuration> {
  return load(
    `/v2/instances/${modelInstanceId}/executive/whatif_curation`,
    undefined,
    API_TYPE.DEFAULT
  ) as Promise<WhatIfCuration>;
}

export async function putWhatIfCuration(
  modelInstanceId: number,
  payload: {
    output_ids: number[];
    input_ids: number[];
  }
): Promise<{ status: string }> {
  return put<{ status: string }>(
    `/v2/instances/${modelInstanceId}/executive/whatif_curation`,
    payload,
    API_TYPE.DEFAULT
  );
}

// ---------------------------------------------------------------------------
// What-If examples (Agentic-API)
//
// Task 10 has landed (app/controllers/v2/library.py): `GET /v2/library`
// accepts `tool`, server-side-filtering to exactly the What-If example
// rows for this instance, and `POST /v2/library/examples` creates them.
// ---------------------------------------------------------------------------

export async function listWhatIfExamples(
  modelInstanceId: number
): Promise<{ prompts: WhatIfExample[] }> {
  return load(
    `/v2/library?model_instance_id=${modelInstanceId}&tool=${WHATIF_EXAMPLE_TOOL}`,
    undefined,
    API_TYPE.AGENTIC
  ) as Promise<{ prompts: WhatIfExample[] }>;
}

export async function createWhatIfExample(body: {
  model_instance_id: number;
  title: string;
  output_id: number;
  input_id: number;
  change_text: string;
}): Promise<{ prompt_id: number }> {
  return create<{ prompt_id: number }>(
    '/v2/library/examples',
    body,
    API_TYPE.AGENTIC
  );
}

// ---------------------------------------------------------------------------
// Tornado prompts (Agentic-API)
//
// ExecutivePrompt rows discriminated by tool='executive_tornado'. Params
// carries the frozen tornado definition (WaterfallChart minus the scenario
// pair + WaterfallColumns) plus default_* seed keys for the executive's
// Baseline / Compare-to dropdowns. Published from a curated Reports-tab
// tornado report; the server re-reads and validates the report.
// ---------------------------------------------------------------------------

export const TORNADO_TOOL = 'executive_tornado';

export interface TornadoPromptParams {
  WaterfallChart: Record<string, unknown>;
  WaterfallColumns: Array<{
    Order: number;
    Name: string;
    InputNames: string[];
  }>;
  default_scenario_a_id: number;
  default_scenario_b_id: number;
  default_scenario_a_name: string | null;
  default_scenario_b_name: string | null;
  source_report_id: number;
  source_report_name: string | null;
}

export async function createTornadoPrompt(
  reportId: number,
  title: string,
  subtitle: string | null,
  category: string
): Promise<{ prompt_id: number }> {
  return create<{ prompt_id: number }>(
    '/v2/library/tornado',
    { report_id: reportId, title, subtitle, category },
    API_TYPE.AGENTIC
  );
}
